/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { GridState, StateUpdatedEvent } from "@ag-grid-community/core";
import { AgGridReactProps } from "@ag-grid-community/react";
import { transactionTableData } from "gen/endpoints/transactions/transactions";
import { ServerSideTableRequestDataRequest } from "gen/model/serverSideTableRequestDataRequest";
import { TableViewReadData } from "gen/model/tableViewReadData";
import { cloneDeep, isMatchWith, omit } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { DataTableRef } from "shared/components/table/data/DataTable";
import { useDecodeConfig } from "shared/components/table/maybern/MaybernTableGridState";

export const useTableStateAsFormValues = ({
  view,
  ref,
  aiState,
}: {
  view?: TableViewReadData;
  ref: React.MutableRefObject<DataTableRef | null>;
  aiState?: GridState;
}) => {
  const viewState = useDecodeConfig(view?.encoded_table_params);
  // for now, ignore the AI while looking at a view til we figure out how to manage the state properly
  const initialState = viewState || aiState;
  const tableKey = view?.id || JSON.stringify(cloneDeep(initialState));

  const [gridState, setGridState] = useState<GridState>({});
  const [defaultValuesGridState, setDefaulValueGridState] = useState<GridState>(
    {},
  );

  const [isPivotDataEmpty, setPivotEmpty] = useState(false);

  const transformedState = useMemo(() => {
    if (!initialState) {
      return {};
    }
    // doing this so sidebar is always closed
    return omit(initialState, "sideBar") as GridState;
  }, [initialState]);

  // table state utils
  const onTableStateUpdated: AgGridReactProps["onStateUpdated"] = useCallback(
    (event: StateUpdatedEvent) => {
      setGridState(event.state);
      // this will re-init anytime data is loaded into grid (e.g. if rows were empty and then data is loaded when switching between pivot cols or filters)
      if (event.sources.includes("gridInitializing")) {
        setDefaulValueGridState(event.state);
      }
    },
    [],
  );

  const syncTableStateWithCurrentView = useCallback(() => {
    const stateUpdate = ref.current?.tableRef?.api.getState();
    if (stateUpdate) {
      setDefaulValueGridState(stateUpdate);
    }
    // reset warning banner (this is a successful save)
    setPivotEmpty(false);
  }, [ref]);
  // table "form" meta data
  const isDirty = !isMatchWith(
    gridState,
    defaultValuesGridState,
    (objValue, srcValue, key) => {
      if (key === "sideBar") {
        return true;
      }
      if (key === "pivot" && srcValue === undefined) {
        return (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          objValue?.pivotColIds && objValue.pivotColIds.length === 0
        );
      }
    },
  );
  const incompletePivot = gridState.pivot?.pivotColIds.length === 0;
  const isInvalid = incompletePivot;

  const getTableData = useCallback(
    async (params: ServerSideTableRequestDataRequest) => {
      const data = await transactionTableData(params);
      if (data.table_data.rows.length === 0 && params.start_row === 0) {
        setPivotEmpty(true);
      }
      return data;
    },
    [],
  );
  // can save on draft, on dirty, or if pivot data is empty (meaning a user will need to change their config)
  return {
    canSave:
      (!view && !isInvalid) || (isDirty && !isInvalid) || isPivotDataEmpty,
    invalidTableState: isInvalid,
    initialState: transformedState,
    tableKey,
    onTableStateUpdated,
    syncTableStateWithCurrentView,
    shouldShowWarning: isPivotDataEmpty,
    getTableData,
  };
};
